import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Gear, CheckCircle, Warning } from "@phosphor-icons/react";

export default function FinanceSettings() {
  const [form, setForm] = useState(null);
  const [providers, setProviders] = useState([]);
  const [wasteCats, setWasteCats] = useState([]);

  const load = async () => {
    const [s, p, w] = await Promise.all([
      api.get("/finance/settings"),
      api.get("/finance/providers"),
      api.get("/waste-types").catch(() => ({ data: [] })),
    ]);
    setForm({ allow_non_qr_categories: [], ...s.data });
    setProviders(p.data);
    // Distinct waste categories (across all waste types) for the picker.
    setWasteCats([...new Set((w.data || []).map(x => x.category).filter(Boolean))].sort());
  };
  useEffect(() => { load(); }, []);

  const toggle = (key) => {
    const arr = new Set(form.providers_enabled);
    if (arr.has(key)) arr.delete(key); else arr.add(key);
    let next = Array.from(arr);
    let def = form.default_provider;
    if (!next.includes(def)) def = next[0] || "razorpay";
    setForm(f => ({ ...f, providers_enabled: next, default_provider: def }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.providers_enabled.length === 0) { toast.error("Enable at least one provider"); return; }
    try {
      await api.put("/finance/settings", {
        gst_rate: Number(form.gst_rate),
        invoice_prefix: form.invoice_prefix,
        providers_enabled: form.providers_enabled,
        default_provider: form.default_provider,
        upi_qr_image: form.upi_qr_image || null,
        upi_id: form.upi_id || null,
        business_name_for_upi: form.business_name_for_upi || null,
        invoice_verification_required: !!form.invoice_verification_required,
        allow_non_qr_categories: form.allow_non_qr_categories || [],
      });
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUpiUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    if (file.size > 1024 * 1024) { toast.error("Max 1 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, upi_qr_image: reader.result }));
    reader.readAsDataURL(file);
  };

  if (!form) return <AppShell title="Finance Settings"><div className="p-6">Loading…</div></AppShell>;

  return (
    <AppShell title="Finance Settings" subtitle="Module 12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={save} className="bg-white border border-[#E5E2D9] rounded-md p-6 space-y-5" data-testid="finance-settings-form">
          <div className="flex items-center gap-2"><Gear size={18} color="#2D5A27" weight="fill"/><div className="font-heading text-lg">Billing</div></div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">GST rate (%)</label>
            <input data-testid="gst-rate" type="number" step="0.1" required value={form.gst_rate}
                   onChange={e => setForm(f => ({ ...f, gst_rate: e.target.value }))}
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
            <div className="text-[11px] text-[#8A8780] mt-1">Applied to every new invoice's subtotal.</div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Invoice number prefix</label>
            <input data-testid="invoice-prefix" required value={form.invoice_prefix}
                   onChange={e => setForm(f => ({ ...f, invoice_prefix: e.target.value }))}
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
            <div className="text-[11px] text-[#8A8780] mt-1">Format: <code>{form.invoice_prefix}-YYYY-NNNNNN</code></div>
          </div>

          <div className="pt-3 border-t border-[#E5E2D9]">
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Field-staff invoice verification</label>
            <div className="flex gap-2">
              {[
                { v: true,  l: "ON — Office reviews every field draft" },
                { v: false, l: "OFF — Field invoices issued immediately" },
              ].map((x) => (
                <button type="button" key={String(x.v)}
                        onClick={() => setForm(f => ({ ...f, invoice_verification_required: x.v }))}
                        data-testid={`inv-verify-${x.v ? "on" : "off"}`}
                        className={`flex-1 h-10 px-3 rounded-md text-xs border transition-colors ${form.invoice_verification_required === x.v ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>
                  {x.l}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-[#8A8780] mt-1.5">
              When ON, field-generated invoices land in <Link to="/finance/invoices-pending" className="text-[#2D5A27] hover:underline">Pending → Drafts</Link> and require office approval before the customer sees them.
            </div>
          </div>

          <div className="pt-3 border-t border-[#E5E2D9]">
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
              Allow non-QR collection for these waste categories
            </label>
            <div className="text-[11px] text-[#8A8780] mb-3">
              Field staff will see an extra &quot;Manual weight entry&quot; panel on the
              collection screen for the selected categories — even when the
              customer has QR bags assigned. Useful for D-BMW / Biomedical
              waste where bulky items don&apos;t fit in a bag.
            </div>
            <div className="grid grid-cols-2 gap-1.5" data-testid="allow-non-qr-cats">
              {wasteCats.length === 0 && (
                <div className="col-span-2 text-[11px] text-[#8A8780] italic">No waste categories configured yet.</div>
              )}
              {wasteCats.map((cat) => {
                const on = (form.allow_non_qr_categories || []).includes(cat);
                return (
                  <label key={cat} data-testid={`non-qr-cat-${cat}`}
                          className={`flex items-center gap-2 text-xs px-2.5 py-2 rounded-md border cursor-pointer transition-colors ${
                            on ? "border-[#2D5A27] bg-[#D4E0D1]/40" : "border-[#E5E2D9]"
                          }`}>
                    <input type="checkbox" checked={on}
                            onChange={(e) => setForm((f) => ({
                              ...f,
                              allow_non_qr_categories: e.target.checked
                                ? [...(f.allow_non_qr_categories || []), cat]
                                : (f.allow_non_qr_categories || []).filter((c) => c !== cat),
                            }))}
                            className="w-4 h-4 accent-[#2D5A27]"/>
                    <span>{cat}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">Payment providers</div>
            <div className="space-y-2">
              {providers.map(p => {
                const enabled = form.providers_enabled.includes(p.key);
                const isDefault = form.default_provider === p.key;
                return (
                  <div key={p.key} className={`border rounded-md p-3 flex items-center gap-3 ${enabled ? "border-[#2D5A27] bg-[#D4E0D1]/30" : "border-[#E5E2D9]"}`}
                       data-testid={`provider-row-${p.key}`}>
                    <input type="checkbox" checked={enabled} onChange={() => toggle(p.key)}
                           data-testid={`provider-toggle-${p.key}`}
                           className="accent-[#2D5A27] w-4 h-4"/>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{p.label}</span>
                        {p.demo && <span className="text-[9px] uppercase tracking-[0.18em] bg-[#C25D38]/15 text-[#C25D38] px-1.5 py-0.5 rounded">DEMO MODE</span>}
                      </div>
                      <div className="text-[11px] text-[#8A8780]">
                        {enabled ? "Enabled — usable for payment orders" : "Disabled"}
                      </div>
                    </div>
                    {enabled && (
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="radio" name="default_provider" checked={isDefault}
                               onChange={() => setForm(f => ({ ...f, default_provider: p.key }))}
                               data-testid={`provider-default-${p.key}`}
                               className="accent-[#2D5A27]"/>
                        Default
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-[#E5E2D9] pt-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">
              Manual UPI · Customer &ldquo;Pay via UPI app&rdquo; button
            </div>
            <div className="text-[11px] text-[#8A8780] mb-3 -mt-1.5">
              Save your business UPI ID here. The customer&apos;s invoice screen
              shows an <b>Open UPI app to pay</b> button that fires a
              <code className="mx-1 px-1 py-0.5 bg-[#F5F2EA] rounded font-mono">upi://pay</code>
              deep link (GPay, PhonePe, Paytm, BHIM…) and pre-fills the amount &amp; note.
              The saved UPI ID is ALSO printed on PDF invoices next to the optional QR image.
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1.5">UPI ID (VPA)</label>
                <input value={form.upi_id || ""} data-testid="upi-id"
                       onChange={e => setForm(f => ({ ...f, upi_id: e.target.value }))}
                       placeholder="rescigohealth@ibl"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1.5">Business name (PayTo)</label>
                <input value={form.business_name_for_upi || ""} data-testid="upi-business-name"
                       onChange={e => setForm(f => ({ ...f, business_name_for_upi: e.target.value }))}
                       placeholder="Rescigo Health"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>
            </div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1.5">UPI QR image (optional · shown on printed bills only)</label>
            <div className="flex items-start gap-3">
              <div className="w-32 h-32 border-2 border-dashed border-[#E5E2D9] rounded-md flex items-center justify-center bg-white overflow-hidden">
                {form.upi_qr_image ? (
                  <img src={form.upi_qr_image} alt="UPI QR preview" className="max-w-full max-h-full object-contain p-1" data-testid="upi-qr-preview"/>
                ) : (
                  <span className="text-[10px] text-[#8A8780]">No QR</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input type="file" accept="image/*" onChange={e => onUpiUpload(e.target.files?.[0])}
                       data-testid="upi-qr-upload"
                       className="text-xs"/>
                <div className="text-[10px] text-[#8A8780]">PNG/JPG up to 1 MB · printed bottom-left of every PDF & e-bill.</div>
                {form.upi_qr_image && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, upi_qr_image: null }))}
                          data-testid="upi-qr-remove"
                          className="h-8 px-3 text-[11px] border border-[#C25D38] text-[#C25D38] rounded-md hover:bg-[#F9EDE7]">
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <button type="submit" data-testid="finance-save"
                  className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <CheckCircle size={14} weight="bold"/>Save settings
          </button>
        </form>

        <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="flex items-center gap-2 mb-4"><Warning size={18} color="#C25D38" weight="fill"/><div className="font-heading text-lg">Integration status</div></div>
          <div className="space-y-3 text-sm">
            <StatusRow label="Razorpay" status="Demo mode — add RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET to backend/.env"/>
            <StatusRow label="PhonePe" status="Demo mode — add PHONEPE_MERCHANT_ID + PHONEPE_SALT_KEY + PHONEPE_SALT_INDEX"/>
            <StatusRow label="Zoho Payments" status="Demo mode — add ZOHO_CLIENT_ID + ZOHO_CLIENT_SECRET"/>
          </div>
          <div className="mt-6 pt-6 border-t border-[#E5E2D9] text-xs text-[#5C5A55] leading-relaxed">
            <div className="font-medium text-[#1A1A1A] mb-1">How demo mode works</div>
            Payment orders are created in DB and return a mock checkout URL.
            Use "Simulate successful payment" on the invoice page to mark
            transactions paid. Replace <code>payment_providers.py</code> stub
            methods with real SDK calls — no other code change needed.
          </div>
        </div>
      </div>
    </AppShell>
  );
}

const StatusRow = ({ label, status }) => (
  <div className="flex items-start gap-3 p-3 bg-[#F7F6F2] border border-[#E5E2D9] rounded-md">
    <div className="w-2 h-2 rounded-full bg-[#C25D38] mt-1.5"/>
    <div>
      <div className="font-medium">{label}</div>
      <div className="text-[11px] text-[#5C5A55] font-mono">{status}</div>
    </div>
  </div>
);
